import * as React from 'react';

/**
 * WI-86 — the ONE keyboard-activation shape for a non-interactive host that carries a click.
 *
 * The pattern is not new: `router/serviceSelection/pickerPrimitives.tsx`'s `SelectCard` already
 * carried `role="button"` + `tabIndex` + an Enter/Space `onKeyDown`, and it is the shape WI-86's
 * closure criterion 2 names. WI-86 converts seven more groups, and `core-principles.md`
 * "Fix-First" is explicit about what that means — *"If two code paths do the same thing
 * differently, consolidate NOW"* — so the shape is lifted here once instead of being retyped
 * eight times with eight chances to drift.
 *
 * ⚠️ **CORRECTED 2026-09-03.** This docblock used to read *"It was the ONLY site in the tree that
 * had it."* That was FALSE when written. `components/workspace/ReferenceDrawerPanel.tsx`'s
 * citation dagger already carried `role="button"` + `tabIndex={0}` + an Enter/Space `onKeyDown`
 * with `preventDefault` BEFORE this wave, and WI-86 did not converge it. It is converged now, in
 * the same change that corrected this sentence — a uniqueness claim is an assertion and this one
 * was never measured.
 *
 * KNOWN OUT-OF-CLASS EXCEPTION, so the next reader does not read the correction as a to-do:
 * `views/analytics/components/IssuesRiskDetails.tsx` has a narrower hand-rolled copy
 * (`handleRowKeyDown` — Enter/Space, `e.target === e.currentTarget`, `preventDefault`) and its
 * own `&:focus-visible` outline. It is a **`<TableRow>`** — TIER D on WI-86's own list, "the row
 * IS the control" — so it is outside this helper's class by construction: this helper's job is to
 * make a NON-interactive host activatable, and its `role`/`tabIndex` would fight a table row's
 * native semantics. Its local outline is a deliberate second source for the ring and is called
 * out below rather than left to contradict the "one source of truth" paragraph.
 *
 * ⛔ THIS IS FOR HOSTS THAT CANNOT BE A REAL `<button>`. Where a real `<button>` is available it
 * wins and this helper is the wrong tool — `TogglePill` in the same picker file says why in its
 * own docblock: *"It is a real `<button aria-pressed>`, so it is focusable and Space/Enter toggles
 * it without any handler of ours."* Every site WI-86 converts is a container that either holds
 * another interactive control (a folder row with a delete `IconButton`, a dropzone with a file
 * `<input>`, a disclosure header with a chip) — where a nested `<button>` is invalid HTML — or is
 * a drag-and-drop target whose element type is load-bearing. `role="button"` + `tabIndex` is the
 * standard remedy for exactly that case, which is why `SelectCard` uses it.
 *
 * Two properties are deliberate rather than incidental:
 *
 *  - **`e.target !== e.currentTarget` returns early.** Several converted hosts contain their own
 *    interactive children. Without this guard an Enter pressed on a nested `IconButton` would
 *    bubble to the container and fire BOTH handlers — a behaviour change, and WI-86 changes
 *    semantics and reachability only, never what an activation does.
 *  - **`stopPropagation` on the activation branch only.** `BuildStage`'s flow node sits inside
 *    `@xyflow/react`, which attaches its own key handling to the node wrapper. Stopping the key
 *    event we have just consumed keeps an ancestor from acting on it a second time. It cannot
 *    regress anything, because before this helper the key event reached no handler at all: these
 *    hosts were not focusable, so no keydown ever originated on them.
 *
 * The focus ring is NOT set here and must not be. `theme.ts`'s `MuiCssBaseline` carries a global
 * `'*:focus-visible'` rule (style guide §5.2) — outline + offset + ring — so every element this
 * helper makes focusable inherits a visible ring, and a per-site outline would be a second source
 * of truth for it. Callers must not suppress it. ⚠️ Stated as a rule for THIS helper's callers,
 * not as a claim about the tree: `IssuesRiskDetails.tsx`'s `<TableRow>` (the out-of-class
 * exception named above) sets its own `&:focus-visible` outline with `outlineOffset: -2`,
 * because a row's outline draws outside the cell box and is clipped otherwise. It is not a
 * caller of this helper, so it is a documented exception rather than a violation.
 *
 * Callers add the STATE attribute their control actually has — `aria-expanded` on a disclosure,
 * `aria-pressed` on a filter toggle. This helper deliberately owns only role/reachability, because
 * the state is per-site and guessing it here would attach a wrong one somewhere.
 */
export function keyboardActivationProps(
  onActivate: (() => void) | undefined,
  opts: { disabled?: boolean } = {},
): {
  role: 'button';
  tabIndex: number;
  'aria-disabled': true | undefined;
  onKeyDown: (e: React.KeyboardEvent) => void;
} {
  const disabled = opts.disabled === true;
  return {
    role: 'button',
    tabIndex: disabled ? -1 : 0,
    'aria-disabled': disabled ? true : undefined,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (disabled || !onActivate) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      // An activation that originated on a nested control belongs to that control.
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      e.stopPropagation();
      onActivate();
    },
  };
}
